import { supabase } from '@/integrations/supabase/client';

interface VideoRepairResult {
  id: string;
  mux_asset_id: string;
  status: 'repaired' | 'failed' | 'skipped';
  message: string;
  playback_id?: string;
}

/**
 * Repairs videos that have asset IDs but missing playback IDs
 * This typically happens when webhook processing fails
 */
export async function repairMissingPlaybackIds(): Promise<VideoRepairResult[]> {
  const results: VideoRepairResult[] = [];
  
  try {
    // Find all videos with asset IDs but no playback IDs
    const { data: brokenVideos, error } = await supabase
      .from('videos')
      .select('id, mux_asset_id, mux_playback_id, status')
      .not('mux_asset_id', 'is', null)
      .is('mux_playback_id', null)
      .limit(20); // Process in batches

    if (error) {
      throw new Error(`Failed to fetch broken videos: ${error.message}`);
    }

    if (!brokenVideos || brokenVideos.length === 0) {
      return [{
        id: 'none',
        mux_asset_id: 'none',
        status: 'skipped',
        message: 'No videos need repair'
      }];
    }

    // Process each broken video
    for (const video of brokenVideos) {
      try {
        // For now, we'll mark them for manual review
        // In production, you'd call Mux API to get asset details
        const result: VideoRepairResult = {
          id: video.id,
          mux_asset_id: video.mux_asset_id,
          status: 'failed',
          message: 'Requires manual Mux API call to retrieve playback_id'
        };

        // Update video status to indicate it needs attention
        await supabase
          .from('videos')
          .update({ 
            status: 'needs_repair',
            updated_at: new Date().toISOString()
          })
          .eq('id', video.id);

        results.push(result);
      } catch (videoError) {
        results.push({
          id: video.id,
          mux_asset_id: video.mux_asset_id,
          status: 'failed',
          message: `Error: ${videoError instanceof Error ? videoError.message : 'Unknown error'}`
        });
      }
    }

    return results;
  } catch (error) {
    throw new Error(`Repair process failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Checks if a video is ready for playback
 */
export async function checkVideoReadiness(videoId: string): Promise<{
  ready: boolean;
  missing: string[];
  video?: any;
}> {
  const { data: video, error } = await supabase
    .from('videos')
    .select('*')
    .eq('id', videoId)
    .single();

  if (error || !video) {
    return { ready: false, missing: ['video not found'] };
  }

  const missing: string[] = [];
  
  if (!video.mux_asset_id) missing.push('mux_asset_id');
  if (!video.mux_playback_id) missing.push('mux_playback_id');
  if (video.status !== 'ready') missing.push(`status is ${video.status}, not ready`);

  return {
    ready: missing.length === 0,
    missing,
    video
  };
}

/**
 * Force update video status based on current data
 */
export async function syncVideoStatus(videoId: string): Promise<{
  success: boolean;
  message: string;
  video?: any;
}> {
  try {
    const { data: video, error } = await supabase
      .from('videos')
      .select('*')
      .eq('id', videoId)
      .single();

    if (error || !video) {
      return { success: false, message: 'Video not found' };
    }

    // Determine correct status based on available data
    let newStatus = video.status;
    
    if (video.mux_asset_id && video.mux_playback_id) {
      newStatus = 'ready';
    } else if (video.mux_asset_id && !video.mux_playback_id) {
      newStatus = 'processing'; // Asset exists but no playback ID yet
    } else if (!video.mux_asset_id) {
      newStatus = 'uploading'; // No asset ID yet
    }

    // Update if status changed
    if (newStatus !== video.status) {
      const { error: updateError } = await supabase
        .from('videos')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', videoId);

      if (updateError) {
        return { success: false, message: `Update failed: ${updateError.message}` };
      }

      return { 
        success: true, 
        message: `Status updated from ${video.status} to ${newStatus}`,
        video: { ...video, status: newStatus }
      };
    }

    return { 
      success: true, 
      message: 'Status already correct',
      video
    };
  } catch (error) {
    return { 
      success: false, 
      message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}
